const SERVER_ACTION_NOT_FOUND_PROMPT_KEY = "vibrant:server-action-not-found-prompt-at";
const SERVER_ACTION_NOT_FOUND_RELOAD_ATTEMPTED_KEY =
  "vibrant:server-action-not-found-reload-attempted";
const SERVER_ACTION_PROMPT_COOLDOWN_MS = 5 * 60 * 1000;
const SERVER_ACTION_RELOAD_PROMPT =
  "The app has been updated since this page loaded.\n\n" +
  "To keep going, refresh this page. If you have unsaved work, choose Cancel and save or copy it first.\n\n" +
  "Refresh this page now?";

interface ServerActionRecoveryWindow {
  addEventListener: Window["addEventListener"];
  confirm: (message?: string) => boolean;
  location: Pick<Location, "reload">;
  sessionStorage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

interface ServerActionRecoveryEvent {
  reason: unknown;
  preventDefault: () => void;
}

function isPromptCoolingDown({
  now,
  previousPromptAt,
}: {
  now: number;
  previousPromptAt?: number;
}): boolean {
  return (
    previousPromptAt !== undefined &&
    Number.isFinite(previousPromptAt) &&
    now - previousPromptAt < SERVER_ACTION_PROMPT_COOLDOWN_MS
  );
}

function clearRecoveryState(
  storage: NonNullable<ServerActionRecoveryWindow["sessionStorage"]>,
): void {
  storage.removeItem(SERVER_ACTION_NOT_FOUND_PROMPT_KEY);
  storage.removeItem(SERVER_ACTION_NOT_FOUND_RELOAD_ATTEMPTED_KEY);
}

/**
 * Detects Next.js stale Server Action IDs from clients that survived a deploy.
 */
export function isServerActionNotFoundError(reason: unknown): boolean {
  if (!(reason instanceof Error)) {
    return false;
  }

  const message = reason.message;
  return (
    reason.name === "UnrecognizedActionError" &&
    message.includes("Server Action") &&
    message.includes("was not found on the server")
  );
}

/**
 * Prompts stale clients to reload so subsequent Server Action submits use the current manifest.
 */
export function handleServerActionNotFoundRejection({
  event,
  win,
  now = Date.now(),
}: {
  event: ServerActionRecoveryEvent;
  win: ServerActionRecoveryWindow;
  now?: number;
}): boolean {
  if (!isServerActionNotFoundError(event.reason)) {
    return false;
  }

  const storage = win.sessionStorage;
  if (!storage) {
    return false;
  }

  const previousPromptAtValue = storage.getItem(SERVER_ACTION_NOT_FOUND_PROMPT_KEY);
  const previousPromptAt = previousPromptAtValue ? Number(previousPromptAtValue) : undefined;
  if (isPromptCoolingDown({ now, previousPromptAt })) {
    if (storage.getItem(SERVER_ACTION_NOT_FOUND_RELOAD_ATTEMPTED_KEY) === "1") {
      clearRecoveryState(storage);
      return false;
    }

    event.preventDefault();
    return true;
  }

  clearRecoveryState(storage);
  event.preventDefault();
  if (!win.confirm(SERVER_ACTION_RELOAD_PROMPT)) {
    storage.setItem(SERVER_ACTION_NOT_FOUND_PROMPT_KEY, String(now));
    return true;
  }

  storage.setItem(SERVER_ACTION_NOT_FOUND_PROMPT_KEY, String(now));
  storage.setItem(SERVER_ACTION_NOT_FOUND_RELOAD_ATTEMPTED_KEY, "1");
  win.location.reload();
  return true;
}

/**
 * Installs the browser recovery listener before Sentry registers global rejection handlers.
 */
export function installServerActionNotFoundRecovery(win: ServerActionRecoveryWindow): void {
  win.addEventListener("unhandledrejection", (event) => {
    handleServerActionNotFoundRejection({
      event: event as PromiseRejectionEvent,
      win,
    });
  });
}

if (typeof window !== "undefined") {
  installServerActionNotFoundRecovery(window);
}
